// @ts-nocheck
import React, { useEffect, useState, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import ListRows from 'components/common/ListRows';
import RectangleRow from 'components/pages/home/RectangleRow';
import CircleRow from 'components/pages/home/CircleRow';

import Ref from 'models/propTypes/ref';
import focusManager from 'support/focus/FocusManager';
import { isPromoRow, isRowWithScoring } from 'support/rowHelper';
import { getHighlightsRowData } from 'support/scoringHelper';
import { useIntl } from 'react-intl';

import { useScoring } from 'hooks/scoring';
import HomeRow from './HomeRow';

import styles from './InteriorRows.module.scss';

const messages = {
  ttsEndOfNavigation: {
    id: 'navigation.tts.ttsEndOfNavigation',
    description: 'TTS text for navigation command cannot be executed',
  },
};

// pageName constants
const SPORTS = 'sports';
const LEAGUES = 'leagues';
const MARKETS = 'markets';
const SHOWS = 'shows';

const InteriorRows = ({
  focusId: focusIdProp,
  onTileClick,
  renderBeforeFirst,
  renderAfterLast,
  onInteriorRowsFocusIndexChanged,
  rowsRef,
  firstRowRef,
  rowData,
  relatedLeagues,
  relatedShows,
  relatedMarkets,
  pageName,
}) => {
  const [focusId, setFocusId] = useState(focusIdProp);

  useEffect(() => {
    if (focusIdProp) {
      setFocusId(focusIdProp);
    } else {
      setFocusId(focusManager.getNewUniqueId());
    }
  }, [focusIdProp]);

  let rows;
  if (pageName === SPORTS) {
    rows = [
      {
        id: 'sportspage-leagues',
        type: 'leagues',
        title: 'Leagues',
        url: '',
        data: relatedLeagues,
      },
      ...rowData,
    ];
  } else if (pageName === LEAGUES) {
    rows = [
      ...rowData,
      {
        id: 'leaguespage-leagues',
        type: 'leagues',
        title: 'Leagues',
        url: '',
        data: relatedLeagues,
      },
    ];
  } else if (pageName === SHOWS) {
    rows = [
      ...rowData,
      {
        id: 'showspage-shows',
        type: 'shows',
        title: 'Shows',
        url: '',
        data: relatedShows,
      },
    ];
  } else if (pageName === MARKETS) {
    rows = [
      ...rowData,
      {
        id: 'marketspage-markets',
        type: 'markets',
        title: 'Markets',
        url: '',
        data: relatedMarkets,
      },
    ];
  }

  const [games, scoringError] = useScoring();

  const renderRow = useCallback(
    (row, rowIndex) => {
      if (!row.data?.length) return null;

      const isPromo = isPromoRow(row);

      let focusIdPrefix;
      if (rowIndex === 0) {
        focusIdPrefix = 'firstrow-';
      } else if (rowIndex === rows.length - 1) {
        focusIdPrefix = 'lastrow-';
      }

      let displayScoring = false;

      let updatedRow = null;
      if (row && isRowWithScoring(row) && !scoringError && games) {
        const highlightsRowData = getHighlightsRowData(row, games);
        if (highlightsRowData.length > 0) {
          displayScoring = true;
          updatedRow = { ...row, data: highlightsRowData };
        }
      }
      updatedRow = updatedRow || row;

      const getHomeRow = () => {
        if (
          row.id === 'sportspage-leagues' ||
          row.id === 'leaguespage-leagues'
        ) {
          return (
            <RectangleRow
              className={styles.row}
              focusIdPrefix={focusIdPrefix}
              displayTitle
              title={
                row.id === 'sportspage-leagues' ? 'Leagues' : 'Related Leagues'
              }
              row={row}
              titleClassName={styles.rectangleRowTitle}
              rowClassName={classNames(styles.row, styles.inverseMargin)}
              rowPos={rowIndex + 1}
            />
          );
        }

        if (row.id === 'showspage-shows' || row.id === 'marketspage-markets') {
          return (
            <CircleRow
              className={styles.row}
              focusIdPrefix={focusIdPrefix}
              displayTitle
              title={row.id === 'showspage-shows' ? 'Shows' : 'Local Markets'}
              row={row}
              titleClassName={styles.circleRowTitle}
              rowClassName={classNames(styles.row, styles.inverseMargin)}
              rowPos={rowIndex + 1}
            />
          );
        }

        return (
          <HomeRow
            row={updatedRow}
            displayTitle
            displayScoring={displayScoring}
            games={games}
            onTileClick={onTileClick}
            key={updatedRow.id}
            ref={rowIndex === 0 ? firstRowRef : null}
            rowClassName={classNames(styles.row, styles.inverseMargin, {
              [styles.promoRow]: isPromo,
            })}
            titleClassName={classNames(styles.rowTitle, {
              [styles.highlightsRowTitle]: displayScoring,
            })}
            tileClassName={styles.tile}
            focusIdPrefix={focusIdPrefix}
            rowPos={rowIndex + 1}
          />
        );
      };

      let firstRowStyle;
      if (pageName === SPORTS) {
        firstRowStyle = styles.firstRowShort;
      } else {
        firstRowStyle = styles.firstRowTall;
      }

      if (rowIndex === 0) {
        return (
          <div className={firstRowStyle}>
            <div className="flex-grow-1" />
            <div className={styles.beforeFirst}>{renderBeforeFirst()}</div>
            {getHomeRow()}
            {rows.length === 1 && renderAfterLast()}
          </div>
        );
      }

      if (
        rowIndex === rows.length - 1 ||
        (rows[rows.length - 1].data === null && rowIndex === rows.length - 2)
      ) {
        return (
          <React.Fragment>
            {getHomeRow()}
            {renderAfterLast()}
          </React.Fragment>
        );
      }
      return getHomeRow();
    },
    [
      firstRowRef,
      onTileClick,
      games,
      renderAfterLast,
      renderBeforeFirst,
      rows,
      scoringError,
      pageName,
    ],
  );

  const shouldSnapRowToTop = useCallback(() => true, []);

  const onListRowsFocusIndexChanged = useCallback(
    index => {
      onInteriorRowsFocusIndexChanged(index);
    },
    [onInteriorRowsFocusIndexChanged],
  );

  const ttsEndOfNavigation = useIntl().formatMessage(
    messages.ttsEndOfNavigation,
  );

  return (
    <React.Fragment>
      <ListRows
        ref={rowsRef}
        focusId={focusId}
        className={styles.listRows}
        data={rows || []}
        renderItem={renderRow}
        buffer={2}
        restorationId={`${focusId}`}
        onListRowsFocusIndexChanged={onListRowsFocusIndexChanged}
        shouldSnapRowToTop={shouldSnapRowToTop}
        ttsEndOfNavigation={ttsEndOfNavigation}
      />
    </React.Fragment>
  );
};

InteriorRows.propTypes = {
  onInteriorRowsFocusIndexChanged: PropTypes.func,
  focusId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onTileClick: PropTypes.func,
  renderBeforeFirst: PropTypes.func,
  renderAfterLast: PropTypes.func,
  firstRowRef: Ref,
  rowsRef: Ref,
  rowData: PropTypes.arrayOf(PropTypes.object),
  relatedLeagues: PropTypes.arrayOf(PropTypes.object),
  relatedShows: PropTypes.arrayOf(PropTypes.object),
  relatedMarkets: PropTypes.arrayOf(PropTypes.object),
  pageName: PropTypes.string,
};

InteriorRows.defaultProps = {
  onInteriorRowsFocusIndexChanged: () => {},
  focusId: null,
  onTileClick: () => {},
  renderBeforeFirst: () => null,
  renderAfterLast: () => null,
  firstRowRef: null,
  rowsRef: null,
  rowData: null,
  relatedLeagues: null,
  relatedShows: null,
  relatedMarkets: null,
  pageName: null,
};

export default React.forwardRef((props, ref) => (
  <InteriorRows {...props} rowsRef={ref} />
));
